/**
 * ActivationBlock — Shared renderer for all brand activation entity blocks
 *
 * Handles: PLAYABLE_AD, BRAND_CHALLENGE, QUEST_SPONSORSHIP, GUILD_SPONSORSHIP
 *
 * These blocks reference a BrandActivation by ID. Since brand activations don't
 * have a public-facing API endpoint yet, this renders a styled placeholder card
 * that shows the activation type and display mode. When public activation endpoints
 * are added, this component will fetch and render live activation data.
 */

import { SurfaceContentBlockType } from "@/hooks/hub/useGetHub";

interface ActivationBlockProps {
    /** BrandActivation document ID */
    activationId?: string;
    /** Rendering mode: "Card" (default), "Compact", or "Banner" */
    displayMode?: string;
    sectionTitle?: string;
    /** Which activation block type this is — determines badge color and label */
    blockType: SurfaceContentBlockType;
}

const ACTIVATION_STYLES: Record<string, { label: string; color: string; icon: string }> = {
    PLAYABLE_AD: { label: "Playable Ad", color: "#f59e0b", icon: "🎮" },
    BRAND_CHALLENGE: { label: "Brand Challenge", color: "#ef4444", icon: "🏆" },
    QUEST_SPONSORSHIP: { label: "Sponsored Quest", color: "var(--dp-text-3)", icon: "📜" },
    GUILD_SPONSORSHIP: { label: "Guild Sponsor", color: "#06b6d4", icon: "🛡" },
};

export function ActivationBlock({ activationId, displayMode = "Card", sectionTitle, blockType }: ActivationBlockProps) {
    if (!activationId) return null;

    const mode = displayMode?.toLowerCase() || "card";
    const style = ACTIVATION_STYLES[blockType] || { label: "Activation", color: "var(--dp-text-3)", icon: "📌" };
    const isCompact = mode === "compact";
    const isBanner = mode === "banner";

    return (
        <div className={`entity-block entity-block--activation entity-block--${mode}`}>
            {sectionTitle && <h3 className="block-section-title" style={{ color: "var(--hub-text-primary, #fff)", marginBottom: "0.75rem" }}>{sectionTitle}</h3>}
            <div
                style={{
                    background: isBanner
                        ? `linear-gradient(135deg, ${style.color}22, ${style.color}44)`
                        : "var(--hub-card-bg, rgba(255,255,255,0.08))",
                    borderRadius: 12,
                    padding: isCompact ? "1rem" : isBanner ? "2rem 1.5rem" : "1.5rem",
                    border: `1px solid ${style.color}33`,
                    display: isBanner ? "flex" : "block",
                    alignItems: "center",
                    gap: isBanner ? "1.5rem" : undefined,
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: isCompact ? 0 : "0.5rem" }}>
                    <span style={{ fontSize: isCompact ? 16 : 24 }}>{style.icon}</span>
                    <span style={{ background: style.color, color: "#fff", fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>{style.label}</span>
                </div>
                {!isCompact &&
                    <p style={{ color: "var(--hub-text-secondary, rgba(255,255,255,0.6))", fontSize: 13, margin: "0.5rem 0 0", lineHeight: 1.4 }}>
                        Activation coming soon.
                    </p>
                }
            </div>
        </div>
    );
}
